import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./css/Documentary.module.css";
import { Play, Plus, Check, User } from "lucide-react";
import {
  useCheckContentInPlaylist,
  useCreatePlaylist,
} from "../../hooks/usePlayListHooks";
import useAuthStore from "../../store/authStore";
import LoginModal from "../../common/Authenticated/LoginModal";

const DocumentaryCard = ({ documentary }) => {
  const navigate = useNavigate();
  const [isClicked, setIsClicked] = useState(false);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [isInLibraryState, setIsInLibraryState] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { isAuthenticated } = useAuthStore();

  const {
    data: playlistCheck,
    isLoading: checkingPlaylist,
    refetch: refetchPlaylistCheck,
  } = useCheckContentInPlaylist(
    documentary?.id ? "video" : null,
    documentary?.id
  );

  const { mutate: createPlaylist, isLoading: creatingPlaylist } =
    useCreatePlaylist();

  useEffect(() => {
    if (playlistCheck) {
      setIsInLibraryState(playlistCheck.inPlaylist);
    }
  }, [playlistCheck]);

  const handleTitleClick = () => {
    setIsClicked(true);
    navigate(`/video/${documentary.slug}`);
  };

  // Handle login from modal
  const handleLogin = () => {
    setShowLoginModal(false);
    // Redirect to login page
    navigate('/signin');
  };

  const handleAddToLibrary = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    if (isInLibraryState || !documentary?.id || isAddingToLibrary) return;

    setIsAddingToLibrary(true);
    createPlaylist(
      {
        item_type: "video",
        content_id: documentary.id,
        title: `${documentary.title} - Library`,
        description: `Library entry for ${documentary.title}`,
      },
      {
        onSuccess: () => {
          setIsInLibraryState(true);
          refetchPlaylistCheck();
        },
        onSettled: () => {
          setIsAddingToLibrary(false);
        },
      }
    );
  };

  if (!documentary) return null;

  const formatDuration = (duration) => {
    if (!duration) return null;
    
    // Convert duration to string before checking if it includes ":"
    const durationStr = String(duration);
    if (durationStr.includes(":")) return durationStr;

    const totalMinutes = parseInt(durationStr, 10);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  return (
    <>
      <div
        className={`${styles.card} ${isClicked ? styles.clicked : ""}`}
        onClick={handleTitleClick}
      >
        <div className={styles.thumbnailContainer}>
          <img
            src={documentary.computer_image_url || documentary.mobile_image_url}
            alt={documentary.title}
            className={styles.thumbnail}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/fallback-image.jpg";
            }}
          />
        </div>

        <div className={styles.details}>
          <div className={styles.buttonsDiv}>
            <div className={styles.watchNowButton}>
              <Play size={18} className={styles.playIcon} />
              <span>Watch Now</span>
            </div>
            <div
              className={`${styles.addToLibraryButton} ${
                isInLibraryState ? styles.inLibrary : ""
              }`}
              onClick={handleAddToLibrary}
            >
              {isAddingToLibrary || checkingPlaylist ? (
                <div className={styles.loadingIndicator}></div>
              ) : isInLibraryState ? (
                <Check size={18} className={styles.libraryIcon} />
              ) : (
                <Plus size={18} className={styles.libraryIcon} />
              )}
            </div>
          </div>
          
          <h3 className={styles.title}>{documentary.title}</h3>
          <div className={styles.meta}>
            {documentary.duration && (
              <>
                <span>{formatDuration(documentary.duration)}</span>
              </>
            )}
          
            {documentary.category && (
              <>
                <span className={styles.dot}>•</span>
                <span>{documentary.category}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
        message="You have to login first to add this video to your library"
      />
    </>
  );
};

export default DocumentaryCard;